import Box from '@mui/material/Box';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import React, { useEffect, useRef, useState } from 'react';
import { useEventBus } from '../../../hooks/use-event-bus';
import { useUrlBuilder } from '../../../hooks/use-history-intercept';
import useWrapperFrames from '../../../hooks/use-wrapper-frames/hook';
import { WrapperFrame } from '../../../hooks/use-wrapper-frames/types';
import { useWrapperNavigation } from '../../../hooks/use-wrapper-navigation';

interface FrameProps {
  frameName: string;
  render: boolean;
  autoActivate?: boolean;
}

const mergeRefs = (...refs: any[]) => {
  const filteredRefs = refs.filter(Boolean);

  if (!filteredRefs.length) return null;

  if (filteredRefs.length === 0) return filteredRefs[0];

  return (inst: any) => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(inst);
      } else if (ref) {
        ref.current = inst;
      }
    }
  };
};

export const FrameComponent = React.forwardRef<HTMLIFrameElement, FrameProps>(
  ({ frameName, render, autoActivate }, ref) => {
    const { activeFrame, getFrameName, getFrameUrl } = useWrapperNavigation();
    const { selectedLocation } = useLocations();
    const { updateFrame, getFrame, sendActivationEvent } = useWrapperFrames();
    const { listen } = useEventBus();
    const [frame, setFrame] = useState<WrapperFrame>({ ...getFrame(frameName), render });
    const { buildUrl } = useUrlBuilder();
    const frameRef = useRef<HTMLIFrameElement>(null);

    useEffect(
      () =>
        listen('navigate', (event) => {
          if (event.messageType !== 'navigate' || !activeFrame) return;

          const eventData = event.data;
          const frame = getFrameName(eventData.pathname);

          if (frame === frameName && eventData.crossFrame && frameRef.current) {
            const url = buildUrl({
              pathname: getFrameUrl(frame, eventData.pathname),
              search: eventData.search || '',
              hash: eventData.hash || '',
              state: null
            });

            frameRef.current.src = url!;
          }
        }),
      [listen, frameName, activeFrame, buildUrl]
    );

    useEffect(() => {
      updateFrame(frame);
    }, [frame]);

    useEffect(() => {
      setFrame((fr: WrapperFrame) => ({ ...fr, render }));
    }, [render]);

    useEffect(() => {
      if (frame.isLoaded && render && (frameName === activeFrame || autoActivate)) {
        setTimeout(() => {
          sendActivationEvent(frameName);
        }, 1000);
      }
    }, [frame.isLoaded, render, activeFrame, frameName]);

    const getClassName = () => (activeFrame === frameName ? 'iframe' : 'hiddenFrame');

    const handleLoad = () => {
      setFrame({ ...frame, isLoaded: true });
    };

    const handleIsLoading = () => {
      setFrame({ ...frame, isLoaded: false });
    };

    const [defaultSource, setDefaultSource] = useState('');

    useEffect(() => {
      if (selectedLocation?.id && !defaultSource) {
        const url = new URL(frame.defaultSrc, window.location.origin);
        url.searchParams.set('locationId', String(selectedLocation.id));
        setDefaultSource(url.toString());
      }
    }, [selectedLocation?.id, frameName]);

    return (
      <>
        {frame && frame.render && defaultSource && (
          <Box
            component="iframe"
            id={`${frameName}-frame`}
            src={defaultSource}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: activeFrame === frameName ? '100%' : '0',
              height: activeFrame === frameName ? '100%' : '0',
              border: 'none',
              display: activeFrame === frameName ? 'block' : 'none'
            }}
            onLoad={handleLoad}
            onLoadStart={handleIsLoading}
            allowFullScreen
            ref={mergeRefs(frameRef, ref)}
            allow="clipboard-read; clipboard-write"
          />
        )}
      </>
    );
  }
);
